import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  UpdateObligationAssessmentResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateObligationAssessmentResultTRPCInput {
  Id: string;
  Rating?: InputMaybe<number>;
  Rationale?: InputMaybe<string>;
  TestDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure.
 * The tRPC update either succeeds on exactly one row or throws, so a
 * successful response always maps to affected_rows: 1.
 */
const mapTrpcResponseToGraphQL =
  (): UpdateObligationAssessmentResultMutation => {
    return {
      update_obligation_assessment_result: {
        __typename: 'obligation_assessment_result_mutation_response',
        affected_rows: 1,
      },
    };
  };

export const useUpdateObligationAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationAssessmentResult.update.mutationOptions({
      onSuccess: async () => {
        // Invalidation parity with the legacy Apollo evictFields:
        // obligation_assessment_result (+ aggregate) and assessment.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.obligationAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateObligationAssessmentResult: async (
      variables: UpdateObligationAssessmentResultTRPCInput
    ): Promise<UpdateObligationAssessmentResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
